import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { colors } from "configs";
import { base, gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { BadgeProps } from "./Badge.types";

export const Container = styled(Box)<
  Pick<BadgeProps, "color" | "variant" | "type" | "size" | "dot"> & {
    withCloseable?: boolean;
    withPrefix?: boolean;
    withSuffix?: boolean;
  }
>(
  ({
    color,
    variant,
    type,
    size,
    dot,
    withCloseable,
    withPrefix,
    withSuffix,
  }) => {
    const isRounded = type === "rounded";

    const selectedColor = color ? colors[color as never] : null;
    let styleBasedOnColor = {
      background: selectedColor ? selectedColor[50] : base.White,
      borderColor: selectedColor ? selectedColor[200] : gray[300],
      color: selectedColor ? selectedColor[700] : gray[700],
      svgIconColor: selectedColor ? selectedColor[500] : gray[500],
    };
    if (selectedColor) {
      switch (variant) {
        case "outlined": {
          styleBasedOnColor = {
            background: base.White,
            borderColor: selectedColor[600],
            color: selectedColor[700],
            svgIconColor: selectedColor[500],
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnType = {};
    switch (type) {
      case "rounded": {
        styleBasedOnType = {
          borderRadius: "16px",
          borderWidth: "1.5px",
        };
        break;
      }
      default: {
        styleBasedOnType = {
          borderRadius: "6px",
          borderWidth: "1px",
        };
        break;
      }
    }

    let styleBasedOnSize = {};
    switch (size) {
      case "small": {
        styleBasedOnSize = {
          padding: isRounded ? `${rem(2)} ${rem(8)}` : `${rem(2)} ${rem(6)}`,
          fontSize: rem(12),
          lineHeight: rem(18),
        };
        break;
      }
      case "medium": {
        styleBasedOnSize = {
          padding: isRounded ? `${rem(2)} ${rem(10)}` : `${rem(2)} ${rem(8)}`,
          fontSize: rem(14),
          lineHeight: rem(20),
        };
        break;
      }
      case "large": {
        styleBasedOnSize = {
          padding: isRounded ? `${rem(4)} ${rem(12)}` : `${rem(4)} ${rem(10)}`,
          fontSize: rem(14),
          lineHeight: rem(20),
        };
        break;
      }
      default:
        break;
    }

    let styleBasedOnDot = {};
    if (dot) {
      const commonDotStyle = {
        content: "''",
        position: "absolute",
        backgroundColor: styleBasedOnColor.color,
        width: rem(8),
        height: rem(8),
        borderRadius: "50%",
        top: "50%",
        transform: "translateY(-50%)",
      };

      switch (size) {
        case "small": {
          styleBasedOnDot = {
            ...{ "&:before": { ...commonDotStyle, left: "6px" } },
            paddingLeft: rem(18),
          };
          break;
        }
        case "medium": {
          styleBasedOnDot = {
            ...{ "&:before": { ...commonDotStyle, left: "8px" } },
            paddingLeft: rem(22),
          };
          break;
        }
        case "large": {
          styleBasedOnDot = {
            ...{ "&:before": { ...commonDotStyle, left: "10px" } },
            paddingLeft: rem(24),
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnWithPrefix = {};
    if (withPrefix) {
      switch (size) {
        case "small": {
          styleBasedOnWithPrefix = {
            paddingLeft: rem(6),
          };
          break;
        }
        case "medium": {
          styleBasedOnWithPrefix = {
            paddingLeft: rem(8),
          };
          break;
        }
        case "large": {
          styleBasedOnWithPrefix = {
            paddingLeft: rem(10),
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnWithSuffix = {};
    if (withSuffix) {
      switch (size) {
        case "small": {
          styleBasedOnWithSuffix = {
            paddingRight: rem(6),
          };
          break;
        }
        case "medium": {
          styleBasedOnWithSuffix = {
            paddingRight: rem(6),
          };
          break;
        }
        case "large": {
          styleBasedOnWithSuffix = {
            paddingRight: rem(10),
          };
          break;
        }
        default:
          break;
      }
    }

    let styleBasedOnWithCloseable = {};
    if (withCloseable) {
      switch (size) {
        case "small": {
          styleBasedOnWithCloseable = {
            paddingRight: rem(3),
          };
          break;
        }
        case "medium": {
          styleBasedOnWithCloseable = {
            paddingRight: rem(4),
          };
          break;
        }
        case "large": {
          styleBasedOnWithCloseable = {
            paddingRight: rem(6),
          };
          break;
        }
        default:
          break;
      }
    }

    return {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      borderStyle: "solid",
      boxShadow: color ? undefined : shadows.xs,
      fontWeight: 500,

      "& .badge-suffix, & .badge-prefix": {
        "& .MuiIconButton-root": {
          marginLeft: rem(-2),
          padding: rem(2),
          color: styleBasedOnColor.svgIconColor,
        },
        "& .MuiSvgIcon-root": {
          fontSize: rem(12),
          color: styleBasedOnColor.svgIconColor,
        },
      },

      ...styleBasedOnColor,
      ...styleBasedOnType,
      ...styleBasedOnSize,
      ...styleBasedOnDot,
      ...styleBasedOnWithPrefix,
      ...styleBasedOnWithSuffix,
      ...styleBasedOnWithCloseable,
    };
  }
);

export const Prefix = styled(Box)(() => ({
  display: "inline-flex",
  marginRight: rem(4),
}));

export const Suffix = styled(Box)(() => ({
  display: "inline-flex",
  marginLeft: rem(4),
}));
